import React, { useEffect, useMemo, useState } from "react";
import LegalPage from "./LegalPage";
import RedeemPage from "./RedeemPage";
import EnterprisePage from "./EnterprisePage";
import GiftPage from "./GiftPage";
import UnsubscribePage from "./UnsubscribePage";
import PostcardsPage from "./PostcardsPage";

/* ------------------------------------------------------------------ */
/*  Winter Panda — marketing landing page                              */
/*  Brand: Winter Panda by Winter Desktops (winterdesktops.com)        */
/* ------------------------------------------------------------------ */

/* Tiny hash router: #/legal/<id> shows a legal page, anything else = home. */
function useHashRoute(): string {
  const [hash, setHash] = useState(
    typeof window !== "undefined" ? window.location.hash : ""
  );
  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return hash;
}

export default function App() {
  // Hooks must run unconditionally and in a stable order — call first.
  const hash = useHashRoute();

  // Path-based routes (spec: winterdesktops.com/redeem/{token}, /enterprise).
  // Real paths so email/Store links stay clean; path nav is a full page load.
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const redeem = path.match(/^\/redeem\/(.+)$/);
  if (redeem) return <RedeemPage token={decodeURIComponent(redeem[1])} />;
  // Business Prospect module: the firm's personalised gifting page and the
  // one-click unsubscribe reached from promotional email footers.
  const gift = path.match(/^\/gift\/(.+)$/);
  if (gift) return <GiftPage token={decodeURIComponent(gift[1])} />;
  const unsub = path.match(/^\/unsubscribe\/(.+)$/);
  if (unsub) return <UnsubscribePage token={decodeURIComponent(unsub[1])} />;
  if (path === "/enterprise" || path === "/enterprise/") return <EnterprisePage />;
  // The postcard collection, shown before sign-up so visitors can see what a
  // Winter Panda gift actually looks like.
  if (path === "/postcards" || path === "/postcards/") {
    return <PostcardsPage billingUrl={BILLING_URL} />;
  }

  // Hash routes (legal docs) + home.
  const m = hash.match(/^#\/legal\/(privacy|terms|eula)/);
  if (m) return <LegalPage id={m[1]} />;
  return <Home />;
}

const IMG = {
  hero: "/images/image1.png", // "Winter Panda" branded panda
  aurora: "/images/image2.png", // northern-lights winter portal
  desk: "/images/image3.png", // cozy desk with the app running
};

// Legal docs render as on-page routes (#/legal/...), generated from the
// source PDFs via scripts/parse_legal.py into src/legal/content.ts.
const DOCS = {
  eula: "#/legal/eula",
  privacy: "#/legal/privacy",
  terms: "#/legal/terms",
};

const STEAM_URL = "https://store.steampowered.com/app/4677820/Winter_Panda/";
const MS_STORE_URL = "https://apps.microsoft.com/detail/9N7CQS5CGX7K";
/**
 * The public site sends visitors to Steam or the Microsoft Store; the direct
 * download is hidden here at the client's request. Set this to true to bring the
 * "Download for Windows" buttons back. Gift recipients download from their own
 * redemption page, which this flag does not affect.
 */
const SHOW_DIRECT_DOWNLOAD = false;
/**
 * Winter Desktops' own Windows installer. Hosted by us rather than a store so
 * complimentary licences aren't capped by store free-key limits.
 */
const DOWNLOAD_URL =
  process.env.REACT_APP_DOWNLOAD_URL || "https://winterdesktops.com/download/WinterPanda-Setup.exe";
/** Where "Gift your panda" leads: the collection first, sign-up from there. */
const GIFT_TEMPLATES_URL = "/postcards";
/** The business billing/gifting portal, reached from the collection page. */
const BILLING_URL = "https://app.winterdesktops.com/billing";
/** Winter Desktops' office on Google Maps (shown in the footer). */
const MAPS_URL = "https://maps.app.goo.gl/9PVeMvyEkM2fJyD97";

/* ---------- scroll reveal hook ---------- */
function useReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ---------- falling snow ---------- */
function Snow() {
  const flakes = useMemo(() => {
    const out: React.CSSProperties[] = [];
    for (let i = 0; i < 42; i++) {
      const size = 2 + ((i * 7) % 5);
      out.push({
        left: `${(i * 2.37) % 100}%`,
        width: `${size}px`,
        height: `${size}px`,
        animationDuration: `${9 + ((i * 13) % 12)}s`,
        animationDelay: `${-((i * 17) % 14)}s`,
        opacity: 0.35 + ((i % 5) * 0.12),
      });
    }
    return out;
  }, []);
  return (
    <div className="snow" aria-hidden="true">
      {flakes.map((style, i) => (
        <span key={i} className="snowflake" style={style} />
      ))}
    </div>
  );
}

/* ---------- shared CTA button ---------- */
function DownloadButton({
  size = "md",
  variant = "cozy",
  className = "",
}: {
  size?: "md" | "lg";
  variant?: "cozy" | "ghost";
  className?: string;
}) {
  const base =
    "group relative inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full font-semibold tracking-tight transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-aurora-cyan/70";
  const pad = size === "lg" ? "px-9 py-4 text-base" : "px-6 py-3 text-sm";
  const look =
    variant === "cozy"
      ? "bg-gradient-to-br from-cozy-300 via-cozy-400 to-cozy-500 text-midnight-900 shadow-cozy hover:-translate-y-0.5 hover:shadow-[0_24px_60px_-14px_rgba(227,168,87,0.7)]"
      : "glass text-frost-100 hover:bg-white/10 hover:-translate-y-0.5";
  return (
    <a
      href={STEAM_URL}
      target="_blank"
      rel="noreferrer"
      className={`${base} ${pad} ${look} ${className}`}
    >
      <SteamIcon className="h-4 w-4" />
      <span>Get it on Steam</span>
    </a>
  );
}

function MenuGlyph({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
      <path
        d={open ? "M6 6l12 12" : "M4 7h16"}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d={open ? "M6 18L18 6" : "M4 12h16"}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      {!open && (
        <path d="M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      )}
    </svg>
  );
}

/* ---------- Direct download from winterdesktops.com ---------- */
function DirectDownloadButton({
  size = "md",
  className = "",
}: {
  size?: "md" | "lg";
  className?: string;
}) {
  const base =
    "group relative inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full font-semibold tracking-tight transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-aurora-teal/70";
  const pad = size === "lg" ? "px-9 py-4 text-base" : "px-6 py-3 text-sm";
  const look =
    "bg-white/[0.08] text-frost-50 ring-1 ring-white/15 hover:-translate-y-0.5 hover:bg-white/[0.12] hover:shadow-[0_20px_50px_-16px_rgba(94,234,212,0.4)]";
  return (
    <a href={DOWNLOAD_URL} className={`${base} ${pad} ${look} ${className}`}>
      <DownloadGlyph className="h-4 w-4 text-aurora-teal" />
      <span>Download for Windows</span>
    </a>
  );
}

function DownloadGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ---------- "Get it on Microsoft Store" CTA ---------- */
function MicrosoftButton({
  size = "md",
  className = "",
  compact = false,
}: {
  size?: "md" | "lg";
  className?: string;
  /** Logo-only until `xl` — used in the nav so it fits beside the Steam button. */
  compact?: boolean;
}) {
  const base =
    "group relative inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full font-semibold tracking-tight transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-aurora-cyan/70";
  const pad =
    size === "lg"
      ? "px-9 py-4 text-base"
      : compact
        ? "px-3.5 py-3 xl:px-6 text-sm"
        : "px-6 py-3 text-sm";
  const look =
    "bg-white/[0.08] text-frost-50 ring-1 ring-white/15 hover:-translate-y-0.5 hover:bg-white/[0.12] hover:shadow-[0_20px_50px_-16px_rgba(103,232,249,0.4)]";
  return (
    <a
      href={MS_STORE_URL}
      target="_blank"
      rel="noreferrer"
      aria-label="Get it on Microsoft Store"
      className={`${base} ${pad} ${look} ${className}`}
    >
      <WindowsIcon className="h-4 w-4" />
      <span className={compact ? "hidden xl:inline" : ""}>Get it on Microsoft Store</span>
    </a>
  );
}

function WindowsIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#F25022" d="M3 3h8.4v8.4H3z" />
      <path fill="#7FBA00" d="M12.6 3H21v8.4h-8.4z" />
      <path fill="#00A4EF" d="M3 12.6h8.4V21H3z" />
      <path fill="#FFB900" d="M12.6 12.6H21V21h-8.4z" />
    </svg>
  );
}

/* ---------- "Gift your panda" CTA → business billing portal ---------- */
function GiftButton({
  size = "md",
  className = "",
}: {
  size?: "md" | "lg";
  className?: string;
}) {
  const base =
    "group relative inline-flex items-center justify-center gap-2.5 overflow-hidden rounded-full font-semibold tracking-tight transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-aurora-violet/70";
  const pad = size === "lg" ? "px-9 py-4 text-base" : "px-6 py-3 text-sm";
  const look =
    "glass text-frost-50 ring-1 ring-white/15 hover:-translate-y-0.5 hover:bg-white/10 hover:shadow-[0_20px_50px_-16px_rgba(167,139,250,0.55)]";
  return (
    <a href={GIFT_TEMPLATES_URL} className={`${base} ${pad} ${look} ${className}`}>
      {/* aurora sheen that warms up on hover */}
      <span className="pointer-events-none absolute inset-0 bg-aurora-band bg-[length:200%_200%] opacity-0 transition-opacity duration-300 animate-aurora group-hover:opacity-[0.18]" />
      <GiftIcon className="relative h-4 w-4 text-aurora-violet" />
      <span className="relative">Gift your panda</span>
    </a>
  );
}

function GiftIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M20 12v7.4a1 1 0 01-1 1H5a1 1 0 01-1-1V12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M3 8h18v4H3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 8v12.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path
        d="M12 8S10.8 3.5 8.2 3.5a2.3 2.3 0 100 4.5H12zM12 8s1.2-4.5 3.8-4.5a2.3 2.3 0 110 4.5H12z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ---------- tiny inline icons ---------- */
function SnowIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M12 2v20M4.2 6.5l15.6 9M19.8 6.5l-15.6 9"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M12 6.5l2.2-2.2M12 6.5l-2.2-2.2M12 17.5l2.2 2.2M12 17.5l-2.2 2.2"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SteamIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 2a10 10 0 00-9.96 9.06l5.34 2.2a2.83 2.83 0 011.6-.5l2.38-3.45v-.05a3.77 3.77 0 113.77 3.77h-.09l-3.4 2.43a2.84 2.84 0 11-5.58.78l-3.82-1.58A10 10 0 1012 2zm-4.9 15.16l-1.22-.5a2.13 2.13 0 002.98 1.1 2.13 2.13 0 00-.86-4.06l1.26.52a1.57 1.57 0 11-2.16 2.94zm8.67-7.62a2.51 2.51 0 11-2.51-2.51 2.51 2.51 0 012.51 2.51zm-4.39 0a1.88 1.88 0 101.88-1.88 1.88 1.88 0 00-1.88 1.88z" />
    </svg>
  );
}

type Feat = { icon: React.ReactNode; title: string; body: string };

/* ================================================================== */
const NAV_LINKS: [string, string][] = [
  ["Experience", "#experience"],
  ["Features", "#features"],
  ["The ritual", "#ritual"],
  ["Privacy", "#privacy"],
  // Gifting moved from a header button to a link so both store CTAs fit; it
  // scrolls to the section that explains the offer before asking for a click.
  ["Gift", "#gift"],
];

function Home() {
  useReveal();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On a fresh load with a section hash (e.g. .../#gift from the app's "Gift
  // your panda" menu), scroll to that section — the browser can't do it itself
  // because the anchor doesn't exist until React has rendered.
  //
  // Scrolling once isn't enough: the artwork above still has no height when the
  // first attempt runs, so the page grows underneath and the visitor is left in
  // the wrong section (that is why the app's link landed on Privacy). Re-aim
  // while the page settles, and stop the moment the visitor scrolls themselves.
  useEffect(() => {
    const id = window.location.hash.replace(/^#/, "");
    if (!id || id.startsWith("/")) return;

    let active = true;
    const aim = () => {
      if (!active) return;
      const el = document.getElementById(id);
      // "auto" so a late correction is an instant nudge, not a visible re-scroll.
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    };
    const stop = () => {
      active = false;
    };

    // Any deliberate scroll means the visitor has taken over.
    const userEvents = ["wheel", "touchstart", "keydown"] as const;
    userEvents.forEach((e) => window.addEventListener(e, stop, { passive: true, once: true }));
    window.addEventListener("load", aim);
    const timers = [0, 150, 400, 900, 1600].map((ms) => window.setTimeout(aim, ms));

    return () => {
      stop();
      userEvents.forEach((e) => window.removeEventListener(e, stop));
      window.removeEventListener("load", aim);
      timers.forEach(window.clearTimeout);
    };
  }, []);

  // Close the mobile menu on Escape and lock body scroll while it's open.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const features: Feat[] = [
    {
      icon: <GlyphHeart />,
      title: "A gentle daily moment",
      body: "Once a day, Winter Panda offers a quiet check-in — a warm little ritual to start your day, never a nag.",
    },
    {
      icon: <GlyphShield />,
      title: "Privacy-first by design",
      body: "It never reads your typing, files, or apps. Activity signals stay on your device — no profiling, no tracking.",
    },
    {
      icon: <GlyphSpark />,
      title: "Lives with your rhythm",
      body: "Active, idle, or asleep — the panda reacts to your day with hand-crafted animations and cozy winter moods.",
    },
    {
      icon: <GlyphFeather />,
      title: "Featherweight & calm",
      body: "A lightweight ambient companion that sits quietly on your desktop and never gets in the way of your work.",
    },
    {
      icon: <GlyphSound />,
      title: "Sights & sounds of winter",
      body: "Snowfall, aurora skies, and a soft hand-illustrated panda — paired with calming ambient nature sounds you can switch on to soothe your focus.",
    },
    {
      icon: <GlyphInfinity />,
      title: "Yours, simply",
      body: "No account to create and nothing to remember. Install it once and it's instantly, quietly yours.",
    },
  ];

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-night-fade font-sans text-frost-100">
      <Snow />

      {/* ---------------- NAV ---------------- */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
          {/* Three flex columns — brand, links, actions. Everything stays in
              normal flow so the links can never overlap the store buttons the
              way an absolutely-centred row does once the CTAs get wide. */}
          <nav
            className={`relative flex items-center gap-4 rounded-full px-4 py-2.5 backdrop-blur-md transition-all duration-500 sm:px-5 ${
              scrolled || menuOpen
                ? "bg-white/[0.06] shadow-glass ring-1 ring-white/15"
                : "bg-white/[0.03] ring-1 ring-white/10"
            }`}
          >
            {/* aurora hairline along the top edge */}
            <span className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

            <a
              href="#top"
              className="group flex flex-none items-center gap-2.5"
              onClick={() => setMenuOpen(false)}
            >
              <BrandMark />
              <span className="flex flex-col leading-none">
                <span className="font-display text-lg font-semibold tracking-tight text-frost-50">
                  Winter Panda
                </span>
                <span className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.22em] text-frost-200/45">
                  Winter Desktops
                </span>
              </span>
            </a>

            {/* nav links — take the middle column and centre within it */}
            <div className="hidden flex-1 items-center justify-center gap-6 lg:flex xl:gap-8">
              {NAV_LINKS.map(([label, href]) => (
                <NavLink key={href} href={href} label={label} />
              ))}
            </div>

            {/* Both stores, always reachable from the top of the page. The
                Microsoft button shows its logo only until there's room for the
                full label. */}
            <div className="hidden flex-none items-center gap-2.5 lg:flex">
              <MicrosoftButton compact />
              <DownloadButton />
            </div>

            {/* mobile hamburger */}
            <button
              type="button"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="ml-auto inline-flex h-10 w-10 flex-none items-center justify-center rounded-full text-frost-50 ring-1 ring-white/15 transition-colors hover:bg-white/10 lg:hidden"
            >
              <MenuGlyph open={menuOpen} />
            </button>
          </nav>
        </div>
      </header>

      {/* ---------------- MOBILE MENU ---------------- */}
      <div
        className={`fixed inset-0 z-40 lg:hidden ${menuOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        {/* scrim */}
        <div
          onClick={() => setMenuOpen(false)}
          className={`absolute inset-0 bg-midnight-900/70 backdrop-blur-sm transition-opacity duration-300 ${
            menuOpen ? "opacity-100" : "opacity-0"
          }`}
        />
        {/* panel */}
        <div
          className={`absolute inset-x-4 top-24 origin-top rounded-3xl glass p-5 shadow-glass transition-all duration-300 ${
            menuOpen ? "translate-y-0 opacity-100" : "-translate-y-3 opacity-0"
          }`}
        >
          <div className="flex flex-col">
            {NAV_LINKS.map(([label, href]) => (
              <a
                key={href}
                href={href}
                onClick={() => setMenuOpen(false)}
                className="rounded-2xl px-4 py-3 text-base font-medium text-frost-100 transition-colors hover:bg-white/10"
              >
                {label}
              </a>
            ))}
          </div>
          {/* Both stores plus gifting — on mobile this menu is the only place
              these actions are reachable from the top of the page. */}
          <div className="mt-4 flex flex-col gap-3 border-t border-white/10 pt-5">
            <DownloadButton className="w-full" />
            <MicrosoftButton className="w-full" />
            {SHOW_DIRECT_DOWNLOAD && <DirectDownloadButton className="w-full" />}
            <GiftButton className="w-full" />
          </div>
        </div>
      </div>

      {/* ---------------- HERO ---------------- */}
      <section id="top" className="relative pt-36 pb-24 sm:pt-44 sm:pb-32">
        {/* aurora glow blobs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-aurora-cyan/20 blur-[120px]" />
          <div className="absolute top-10 right-1/4 h-80 w-80 rounded-full bg-aurora-violet/20 blur-[130px]" />
        </div>

        <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="reveal text-center lg:text-left">
            <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-frost-200">
              <span className="h-1.5 w-1.5 rounded-full bg-aurora-teal" />
              A cozy desktop companion
            </span>
            <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.05] tracking-tight text-frost-50 sm:text-6xl lg:text-7xl">
              Bring a little
              <br />
              <span className="text-aurora">winter warmth</span>
              <br />
              to your day.
            </h1>
            <p className="mx-auto mt-7 max-w-xl text-lg leading-relaxed text-frost-200/85 lg:mx-0">
              Winter Panda is a calm, privacy-first companion that lives on your
              desktop — reacting to your rhythm with gentle animations and one
              warm daily check-in. No accounts. No tracking. Just a quiet pocket
              of winter.
            </p>
            <div className="mt-9 flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap lg:justify-start sm:justify-center">
              <DownloadButton size="lg" />
              <MicrosoftButton size="lg" />
              {SHOW_DIRECT_DOWNLOAD && <DirectDownloadButton size="lg" />}
              <a
                href="#experience"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-frost-100"
              >
                See the experience
                <span className="transition-transform group-hover:translate-x-1">
                  →
                </span>
              </a>
            </div>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-sm text-frost-200/70 lg:justify-start">
              <Trust label="Stays on your device" />
              <Trust label="No account needed" />
              <Trust label="Lightweight" />
            </div>
          </div>

          {/* hero visual */}
          <div className="reveal relative mx-auto w-full max-w-md">
            <div className="absolute inset-0 -z-10 rounded-[2.5rem] bg-aurora-band bg-[length:200%_200%] opacity-30 blur-2xl animate-aurora" />
            <div className="animate-float-slow rounded-[2.25rem] glass p-3 shadow-glass">
              <img
                src={IMG.hero}
                alt="Winter Panda — a soft, snowy panda companion"
                className="w-full rounded-[1.75rem] object-cover"
              />
            </div>
            {/* floating live-status widget */}
            <div className="absolute -bottom-7 -left-5 hidden animate-float sm:block">
              <div className="relative overflow-hidden rounded-2xl glass px-5 py-4 shadow-glass">
                {/* aurora hairline accent */}
                <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-aurora-cyan/70 to-transparent" />
                {/* soft moon watermark */}
                <MoonGlyph className="pointer-events-none absolute -right-2 -top-2 h-16 w-16 rotate-12 text-aurora-cyan/10" />
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-aurora-teal/70" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-aurora-teal" />
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-frost-200/55">
                      Right now
                    </span>
                  </div>
                  <p className="mt-2 text-base font-semibold leading-none text-frost-50">
                    Resting by the window
                  </p>
                  <p className="mt-2 text-[11px] font-medium tracking-wide text-frost-200/45">
                    Calm &middot; gently idle
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- MARQUEE / VALUES STRIP ---------------- */}
      <section className="border-y border-white/5 bg-white/[0.02] py-6">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-center gap-x-10 gap-y-4 px-5 text-sm font-medium text-frost-200/60 sm:px-8">
          {[
            "Reacts to active, idle & sleep",
            "Hand-crafted animations",
            "Calming nature sounds",
            "Local-only signals",
            "One gentle prompt a day",
            "No mood detection",
          ].map((t) => (
            <span key={t} className="flex items-center gap-2">
              <SnowIcon className="h-3.5 w-3.5 text-aurora-cyan/70" />
              {t}
            </span>
          ))}
        </div>
      </section>

      {/* ---------------- EXPERIENCE (desk image) ---------------- */}
      <section id="experience" className="relative py-24 sm:py-32">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
          <div className="reveal mx-auto max-w-2xl text-center">
            <Eyebrow>The experience</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-frost-50 sm:text-5xl">
              A warm companion, right where you work
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-frost-200/80">
              Winter Panda settles into the corner of your screen and quietly
              shares your day — a soft, living presence amid the busy hours.
            </p>
          </div>

          <div className="reveal mt-14 overflow-hidden rounded-[2rem] glass p-2.5 shadow-glass">
            <div className="relative overflow-hidden rounded-[1.6rem]">
              <img
                src={IMG.desk}
                alt="Winter Panda running on a desktop in a cozy winter workspace"
                className="w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-midnight-900/60 via-transparent to-transparent" />
            </div>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-3">
            {[
              ["Always present", "A gentle figure on your desktop, never demanding your attention."],
              ["Softly responsive", "It notices the flow of your day and shifts its mood to match."],
              ["Calmly yours", "A small comfort that makes long winter work hours feel cozier."],
            ].map(([t, b], i) => (
              <div
                key={t}
                className="reveal glass rounded-2xl p-6"
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <h3 className="font-display text-xl font-semibold text-frost-50">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-frost-200/75">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FEATURES GRID ---------------- */}
      <section id="features" className="relative py-24 sm:py-32">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
          <div className="reveal mx-auto max-w-2xl text-center">
            <Eyebrow>Why you'll love it</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-frost-50 sm:text-5xl">
              Small in footprint. Big in warmth.
            </h2>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <article
                key={f.title}
                className="reveal group relative overflow-hidden rounded-3xl glass p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-glow"
                style={{ transitionDelay: `${(i % 3) * 80}ms` }}
              >
                <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-aurora-violet/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100 opacity-0" />
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-white/15 to-white/5 text-aurora-cyan ring-1 ring-white/10">
                  {f.icon}
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-frost-50">
                  {f.title}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-frost-200/75">
                  {f.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- THE DAILY RITUAL ---------------- */}
      <section id="ritual" className="relative py-24 sm:py-32">
        <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <div className="reveal order-2 lg:order-1">
            <Eyebrow>The daily ritual</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-frost-50 sm:text-5xl">
              One warm question,
              <br />
              once a day.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-frost-200/80">
              After a few minutes with you, Winter Panda gently asks how your day
              is going. Answer in a tap — or a single word — and enjoy a soft
              moment of warmth. That's the whole ritual: light, kind, and
              entirely optional.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                ["Gentle timing", "It waits quietly and only appears once a day — never interrupting your flow."],
                ["A single tap", "Reflect with a quick choice or one word. No essays, no pressure."],
                ["Always optional", "Not in the mood? Dismiss it. Winter Panda simply curls back up."],
              ].map(([t, b]) => (
                <li key={t} className="flex gap-4">
                  <span className="mt-1 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-aurora-teal/20 text-aurora-teal">
                    <CheckGlyph />
                  </span>
                  <span>
                    <span className="font-semibold text-frost-50">{t}. </span>
                    <span className="text-frost-200/75">{b}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="reveal order-1 lg:order-2">
            <div className="relative mx-auto max-w-md">
              <div className="absolute inset-0 -z-10 rounded-[2.5rem] bg-aurora-band bg-[length:200%_200%] opacity-25 blur-2xl animate-aurora" />
              <div className="rounded-[2rem] glass p-3 shadow-glass">
                <img
                  src={IMG.aurora}
                  alt="An aurora-lit winter scene"
                  className="w-full rounded-[1.5rem] object-cover"
                />
              </div>
              {/* mock prompt card */}
              <div className="absolute -bottom-7 left-1/2 w-[86%] -translate-x-1/2 overflow-hidden rounded-2xl border border-white/12 bg-gradient-to-b from-midnight-800/95 to-midnight-900/90 p-4 shadow-[0_28px_60px_-22px_rgba(0,0,0,0.75)] backdrop-blur-xl">
                {/* top light sheen */}
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                <div className="flex items-center gap-2.5">
                  <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gradient-to-br from-aurora-cyan/25 to-aurora-violet/20 text-aurora-cyan ring-1 ring-white/15">
                    <SnowIcon className="h-3.5 w-3.5" />
                  </span>
                  <p className="text-sm font-semibold text-frost-50">
                    How's your day been?
                  </p>
                </div>
                <div className="mt-3.5 flex flex-wrap gap-2">
                  {["Great", "Alright", "Not that great"].map((o, i) => (
                    <span
                      key={o}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${
                        i === 0
                          ? "bg-gradient-to-br from-cozy-300 to-cozy-500 text-midnight-900 shadow-[0_6px_16px_-6px_rgba(227,168,87,0.75)]"
                          : "bg-white/20 text-white ring-1 ring-inset ring-white/20"
                      }`}
                    >
                      {o}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- PRIVACY ---------------- */}
      <section id="privacy" className="relative py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="reveal relative overflow-hidden rounded-[2.5rem] glass p-10 text-center shadow-glass sm:p-16">
            <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-aurora-teal/15 blur-[100px]" />
            <div className="relative">
              <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-aurora-teal/30 to-aurora-cyan/10 text-aurora-teal ring-1 ring-white/10">
                <GlyphShield />
              </div>
              <h2 className="mt-6 font-display text-4xl font-semibold tracking-tight text-frost-50 sm:text-5xl">
                Built to respect you
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-frost-200/80">
                Winter Panda is a lightweight ambient companion — nothing more.
                It never reads your typing, files, documents, or apps. It does no
                mood detection and no behavioural profiling. Activity signals are
                processed locally on your device.
              </p>
              <div className="mt-9 grid gap-4 sm:grid-cols-3">
                {[
                  ["Local-only", "Signals stay on your device."],
                  ["No content access", "Never reads what you type or open."],
                  ["No profiling", "No mood analysis or tracking."],
                ].map(([t, b]) => (
                  <div key={t} className="rounded-2xl bg-white/[0.04] p-5 ring-1 ring-white/10">
                    <p className="font-display text-lg font-semibold text-frost-50">{t}</p>
                    <p className="mt-1 text-sm text-frost-200/70">{b}</p>
                  </div>
                ))}
              </div>
              <a
                href={DOCS.privacy}
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-aurora-cyan hover:text-frost-50"
              >
                Read our full Privacy Policy →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- GIFT CTA ---------------- */}
      <section id="gift" className="relative py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="reveal relative overflow-hidden rounded-[2.5rem] glass p-10 text-center shadow-glass sm:p-16">
            {/* aurora glows */}
            <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-aurora-violet/20 blur-[110px]" />
            <div className="pointer-events-none absolute -bottom-24 right-1/4 h-56 w-56 rounded-full bg-cozy-400/15 blur-[110px]" />
            <div className="relative">
              <div className="flex items-center justify-center m-auto gap-4">
                <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-frost-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-aurora-violet" />
                  For friends, family &amp; teams
                </span>
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-aurora-violet/30 to-cozy-400/15 text-aurora-violet ring-1 ring-white/10">
                  <GiftIcon className="h-7 w-7" />
                </div>
              </div>
              <h2 className="mt-6 font-display text-4xl font-semibold tracking-tight text-frost-50 sm:text-5xl">
                Give the gift of winter
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-frost-200/80">
                Send a cozy Winter Panda to someone you love — or warm up a whole
                team. Each person gets a gentle desktop companion of their own,
                wrapped in a personal invite.
              </p>
              <div className="mt-9 grid gap-4 text-left sm:grid-cols-3">
                {[
                  ["1 · Choose", "Pick a plan that fits — a single gift or your whole team."],
                  ["2 · Send", "We deliver a warm, branded invite to each recipient by email."],
                  ["3 · Unwrap", "They redeem in a click and their panda settles onto their desktop."],
                ].map(([t, b]) => (
                  <div key={t} className="rounded-2xl bg-white/[0.04] p-5 ring-1 ring-white/10">
                    <p className="font-display text-lg font-semibold text-frost-50">{t}</p>
                    <p className="mt-1 text-sm text-frost-200/70">{b}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10 flex flex-col items-center">
                <GiftButton size="lg" />
                {/* Sets the expectation before they leave the page. */}
                <p className="mt-3 text-xl text-frost-200/65">
                  By creating an account on the next page
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- DOWNLOAD CTA ---------------- */}
      <section id="download" className="relative py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="reveal relative overflow-hidden rounded-[2.5rem] border border-white/10 shadow-glass">
            <img
              src={IMG.aurora}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-midnight-900/85 via-midnight-800/80 to-midnight-700/85" />
            <div className="relative px-8 py-16 text-center sm:px-16 sm:py-20">
              <h2 className="font-display text-4xl font-semibold tracking-tight text-frost-50 sm:text-6xl">
                Bring Winter Panda home
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-frost-200/85">
                Start the day a little warmer. Free to download, gentle by
                design, and quietly yours.
              </p>
              <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row sm:flex-wrap">
                <DownloadButton size="lg" />
                <MicrosoftButton size="lg" />
                {SHOW_DIRECT_DOWNLOAD && <DirectDownloadButton size="lg" />}
              </div>
              <p className="mt-5 text-sm text-frost-200/60">
                Windows 10 &amp; 11 · ~light footprint
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- FOOTER ---------------- */}
      <footer className="border-t border-white/5 bg-white/[0.02]">
        <div className="mx-auto max-w-[1440px] px-5 py-14 sm:px-8">
          <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
            <div>
              <a href="#top" className="flex items-center gap-2.5">
                <BrandMark />
                <span className="font-display text-lg font-semibold text-frost-50">
                  Winter Panda
                </span>
              </a>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-frost-200/65">
                A cozy, privacy-first desktop companion by Winter Desktops.
                A small pocket of winter warmth for your everyday.
              </p>

              {/* Registered office. Shown in full so the business is verifiable,
                  which also helps email deliverability and store listings. */}
              <address className="mt-6 not-italic text-sm leading-relaxed text-frost-200/60">
                <span className="font-semibold text-frost-200/80">Winter Desktops</span>
                <br />
                Floor 9, 225 West 34th Street
                <br />
                14 Pennsylvania Plaza
                <br />
                New York, NY 10122, United States
              </address>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 text-sm text-aurora-cyan transition-colors hover:text-frost-50"
              >
                View on Google Maps
                <span aria-hidden="true">→</span>
              </a>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-frost-200/50">
                Explore
              </p>
              <ul className="mt-4 space-y-3 text-sm text-frost-200/75">
                <li><a href="#experience" className="hover:text-frost-50">Experience</a></li>
                <li><a href="#features" className="hover:text-frost-50">Features</a></li>
                <li><a href="#ritual" className="hover:text-frost-50">The daily ritual</a></li>
                <li><a href="#download" className="hover:text-frost-50">Download</a></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-frost-200/50">
                Legal
              </p>
              <ul className="mt-4 space-y-3 text-sm text-frost-200/75">
                <li><a href={DOCS.privacy} className="hover:text-frost-50">Privacy Policy</a></li>
                <li><a href={DOCS.terms} className="hover:text-frost-50">Terms &amp; Conditions</a></li>
                <li><a href={DOCS.eula} className="hover:text-frost-50">EULA</a></li>
                <li>
                  <a href="mailto:privacy@winterdesktops.com" className="hover:text-frost-50">
                    privacy@winterdesktops.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-5 border-t border-white/5 pt-7 sm:flex-row">
            <p className="text-sm text-frost-200/55">
              © 2026 Winter Desktops. All rights reserved.
            </p>
            <a
              href="mailto:privacy@winterdesktops.com"
              className="text-sm text-frost-200/55 transition-colors hover:text-frost-50"
            >
              privacy@winterdesktops.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------- small presentational helpers ---------- */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-aurora-cyan">
      {children}
    </span>
  );
}

function NavLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      className="group relative whitespace-nowrap py-1 text-sm font-medium tracking-tight text-frost-200/75 transition-colors duration-200 hover:text-frost-50"
    >
      {label}
      <span className="pointer-events-none absolute -bottom-0.5 left-1/2 h-px w-0 -translate-x-1/2 rounded-full bg-gradient-to-r from-aurora-cyan to-aurora-violet transition-all duration-300 group-hover:w-full" />
    </a>
  );
}

function Trust({ label }: { label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-aurora-teal/20 text-aurora-teal">
        <CheckGlyph />
      </span>
      {label}
    </span>
  );
}

function BrandMark() {
  return (
    <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-frost-300/30 to-aurora-violet/20 ring-1 ring-white/15">
      <span className="absolute inset-0 rounded-xl bg-aurora-cyan/20 blur-md" />
      <span className="relative text-lg" role="img" aria-label="panda">
        🐼
      </span>
    </span>
  );
}

/* ---------- glyphs ---------- */
function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-3 w-3">
      <path d="M5 12.5l4 4 10-10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function GlyphHeart() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
      <path d="M12 20s-7-4.4-9.2-8.4C1.2 8.7 2.6 5.5 5.8 5.1 7.9 4.8 9.5 6 12 8.6c2.5-2.6 4.1-3.8 6.2-3.5 3.2.4 4.6 3.6 3 6.5C19 15.6 12 20 12 20z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}
function GlyphShield() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
      <path d="M12 3l7 3v5c0 4.4-3 7.7-7 9-4-1.3-7-4.6-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function GlyphSpark() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}
function GlyphFeather() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
      <path d="M19 5c-4 0-9 2-11 7l-3 6 6-3c5-2 7-7 8-10z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M16 8l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function GlyphSound() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
      <path d="M4 9v6h3l5 4V5L7 9H4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M16 8.5a4 4 0 010 7M18.5 6a7 7 0 010 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function MoonGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 109.8 9.8z" />
    </svg>
  );
}
function GlyphInfinity() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
      <path d="M7 8.5a3.5 3.5 0 100 7c2.5 0 3.5-3.5 5-3.5s2.5 3.5 5 3.5a3.5 3.5 0 100-7c-2.5 0-3.5 3.5-5 3.5s-2.5-3.5-5-3.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}
